import React, { useContext, useEffect, useState } from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import { philippineCurrency, percentageColor, maskAmount } from '../../../helpers/helperFunctions';
// Components
import BorderSection from '../../../components/BorderSection';
import AreaApexChart from '../../../components/AreaApexChart';
import PrdResultTable from './AccountManagementTable/PrdResultTable';
import TouchpointTable from './AccountManagementTable/TouchpointTable';
import CoverageTable from './AccountManagementTable/CoverageTable';
import TicketTrackerTable from './AccountManagementTable/TicketTrackerTable';
import AccountManagementTable from './AccountManagementTable/AccountManagementTable';
// API
import { getMdSalesInfo, getMdAccountManagement, getMdRyg } from '../../../api/mdProfileApi';
// Context
import { useMdProfileContext } from '../context/useMdProfileContext';

export default function AccountManagementTab({ modalFilterForm }) {
  const { viewProfile } = useContext(useMdProfileContext);

  const defaultFilter = {
    doctor_id: viewProfile?.doctor_id,
    year: modalFilterForm?.year,
  };
  const [filterForm, setfilterForm] = useState(defaultFilter);
  const [salesTrend, setSalesTrend] = useState({
    loading: false,
    items: [],
    target: [],
    actual: [],
    total_target: 0,
    total_actual: 0,
    target_percentage: 100,
    actual_percentage: 0,
  });
  const [prdResult, setPrdResult] = useState({ loading: true, items: [] });
  const [engagementTouchpoint, setEngagementTouchpoint] = useState({ loading: false, items: [] });
  const [accountManagement, setAccountManagement] = useState({
    toggle: false,
    loading: false,
    items: [],
  });
  const [coverage, setCoverage] = useState({ loading: false, items: [] });

  const months = [
    'jan',
    'feb',
    'mar',
    'apr',
    'may',
    'jun',
    'jul',
    'aug',
    'sep',
    'oct',
    'nov',
    'dec',
  ];

  const loadPrdInfo = async () => {
    setPrdResult({
      ...prdResult,
      loading: true,
    });
    const request = {
      md_id: viewProfile?.doctor_id,
      year: modalFilterForm?.year,
    };

    const response = await getMdRyg(request);
    const { code, data } = response?.data;

    if (code === '200') {
      setPrdResult({
        ...prdResult,
        loading: false,
        items: [data],
      });
    }
  };
  const loadSalesInfo = async () => {
    setSalesTrend({
      ...salesTrend,
      loading: true,
    });
    const targets = [];
    const actuals = [];

    const request = {
      doctor_id: viewProfile?.doctor_id,
      year: modalFilterForm?.year,
    };
    const response = await getMdSalesInfo(request);
    const { code, data } = response?.data;
    if (code === '00003') {
      const sales = data[0] ?? {};

      months.forEach((month) => {
        const targetKey = `${month}_target`;
        const actualKey = `${month}_actual`;

        if (sales[targetKey] !== undefined) {
          targets.push(Number(sales[targetKey]));
        }

        if (sales[actualKey] !== undefined) {
          actuals.push(Number(sales[actualKey]));
        }
      });

      setSalesTrend({
        ...salesTrend,
        loading: false,
        items: data[0] ?? [],
        target: targets,
        actual: actuals,
        total_target: data[0]?.total_target,
        total_actual: data[0]?.total_actual,
        actual_percentage: parseInt((sales?.total_actual / sales?.total_target) * 100),
      });
    }
  };

  const loadAccountManagement = async () => {
    setEngagementTouchpoint({ ...engagementTouchpoint, loading: true });
    setAccountManagement({ ...accountManagement, loading: true });
    setCoverage({ ...coverage, loading: true });
    const request = {
      md_id: viewProfile?.doctor_id,
      year: modalFilterForm?.year,
    };
    const response = await getMdAccountManagement(request);
    const { code, data } = response?.data;
    if (code === '200') {
      setEngagementTouchpoint({
        ...engagementTouchpoint,
        loading: false,
        items: data.find((row) => row.section_name === 'Total Engagement/Touchpoints'),
      });

      setAccountManagement({
        ...accountManagement,
        toggle: true,
        loading: false,
        items: data.filter(
          (row) =>
            row.section_name !== 'Total Engagement/Touchpoints' && row.section_name !== 'Coverage',
        ),
      });

      setCoverage({
        ...coverage,
        loading: false,
        items: data.find((row) => row.section_name === 'Coverage'),
      });
    }
  };

  const handleGenerateData = async () => {
    loadPrdInfo();
    loadAccountManagement();
    loadSalesInfo();
  };

  useEffect(() => {
    handleGenerateData();
  }, [modalFilterForm]);

  return (
    <>
      <Box mb={4}>
        <Grid container>
          <Grid item sx={12} sm={12} md={4} xl={4}></Grid>
          <Grid item sx={12} sm={12} md={4} xl={4}></Grid>
          <Grid item sx={12} sm={12} md={4} xl={4}>
            <BorderSection>
              <Grid container>
                <Grid item sx={6} sm={6} md={6} xl={6}>
                  <Typography variant='subtitle1' sx={{ fontWeight: 'bold' }}>
                    Annual Sales Target <br />{' '}
                    {maskAmount(philippineCurrency(salesTrend?.total_target ?? 0))}{' '}
                    <span style={{ color: percentageColor(salesTrend?.target_percentage) }}>
                      {salesTrend?.target_percentage}%
                    </span>
                  </Typography>
                </Grid>
                <Grid item sx={6} sm={6} md={6} xl={6}>
                  <Typography variant='subtitle1' sx={{ fontWeight: 'bold' }}>
                    YTD Sales <br /> {maskAmount(philippineCurrency(salesTrend?.total_actual ?? 0))}{' '}
                    <span
                      style={{
                        color: percentageColor(
                          !Number.isNaN(salesTrend?.actual_percentage)
                            ? salesTrend?.actual_percentage
                            : 0,
                        ),
                      }}
                    >
                      {!Number.isNaN(salesTrend?.actual_percentage)
                        ? salesTrend?.actual_percentage
                        : 0}
                      %
                    </span>
                  </Typography>
                </Grid>
              </Grid>
            </BorderSection>
          </Grid>
        </Grid>
      </Box>
      <Box mb={4}>
        <BorderSection title='Sales Trend'>
          {salesTrend?.loading ? (
            <LinearProgress />
          ) : (
            <Box sx={{ paddingLeft: '330px' }}>
              <AreaApexChart
                name={'areaChart'}
                target={salesTrend?.target}
                actual={salesTrend?.actual}
                maskAmountValue={true}
              />
            </Box>
          )}
        </BorderSection>
      </Box>
      <Box mb={4}>
        <PrdResultTable prdResult={prdResult} months={months} />
      </Box>
      <Box mb={4}>
        <TouchpointTable engagementTouchpoint={engagementTouchpoint} />
      </Box>
      {accountManagement?.toggle
        ? accountManagement?.items.map((row) => (
            <Box mb={4}>
              <AccountManagementTable accountManagement={row} filterForm={filterForm} />
            </Box>
          ))
        : ''}
      <Box mb={4}>
        <CoverageTable coverage={coverage} filterForm={filterForm} />
      </Box>
      <Box mb={4}>
        <TicketTrackerTable filterForm={filterForm} />
      </Box>
    </>
  );
}
